import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 bg-night min-h-screen">
      {/* Hero Section */}
      <section className="relative flex flex-col items-center text-center px-6 py-12 bg-night-card border-b border-night-line">
        <motion.h1
          className="font-display text-4xl md:text-6xl font-extrabold mb-4 leading-tight text-mist"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          PRIVACY POLICY
        </motion.h1>
        <motion.div
          className="flex flex-col items-center space-y-2 text-mist-dim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-lg font-semibold">Effective Date: November 18, 2025</p>
          <p className="text-base">Platform: Zuntly Commerce System</p>
          <p className="text-base">Applies To: Merchants & Customers using Zuntly-powered mobile apps</p>
        </motion.div>
      </section>

      {/* Privacy Policy Content */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          className="space-y-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {/* Introduction */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📋</span>
              1. Introduction
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>
                Zuntly provides an e-commerce platform that enables 
                independent businesses ("Merchants") to create online stores and mobile apps.
              </p>
              <p>
                This Privacy Policy explains how we collect, store, use, and safeguard personal information collected:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>from Customers using any Zuntly-powered website or mobile app</li>
                <li>from Merchants who run their businesses on Zuntly</li>
              </ul>
              <p className="font-semibold text-glow">
                By accessing any Zuntly-powered system or app, you agree to this Privacy Policy.
              </p>
            </div>
          </div>

          {/* Section 2: Information We Collect */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📝</span>
              2. Information We Collect
            </h2>
            <div className="text-mist-soft space-y-6 leading-relaxed">
              {/* Customer Information */}
              <div>
                <h3 className="text-xl font-semibold text-mist mb-3">A. Customer Information</h3>
                <p className="mb-2">Collected when users shop on a merchant's store:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Full Name</li>
                  <li>Phone Number</li>
                  <li>Email Address</li>
                  <li>Delivery / Billing Address</li>
                  <li>Order Details</li>
                  <li>Cart Data</li>
                </ul>
              </div>

              {/* Merchant Information */}
              <div>
                <h3 className="text-xl font-semibold text-mist mb-3">B. Merchant Information</h3>
                <p className="mb-2">Collected when merchants manage stores:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Business Name</li>
                  <li>Contact Details</li>
                  <li>GST/PAN (if provided)</li>
                  <li>Store configurations</li>
                </ul>
              </div>

              {/* Technical Information */}
              <div>
                <h3 className="text-xl font-semibold text-mist mb-3">C. Technical & Device Information</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Device model</li>
                  <li>Operating system</li>
                  <li>IP address</li>
                  <li>App version</li>
                  <li>Usage logs</li>
                </ul>
              </div>

              {/* Payment Information */}
              <div>
                <h3 className="text-xl font-semibold text-mist mb-3">D. Payment Information</h3>
                <p className="mb-2 font-semibold">We never store:</p>
                <div className="bg-night-warm border border-night-line rounded-lg p-4 space-y-1">
                  <p className="text-glow-deep flex items-center"><span className="mr-2">❌</span> Card numbers</p>
                  <p className="text-glow-deep flex items-center"><span className="mr-2">❌</span> CVV</p>
                  <p className="text-glow-deep flex items-center"><span className="mr-2">❌</span> UPI PIN</p>
                </div>
                <p className="mt-3 font-medium text-green-700">Payments are securely processed by third-party gateways.</p>
              </div>
            </div>
          </div>

          {/* Section 3: How We Use Information */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">⚙️</span>
              3. How We Use Information
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>Zuntly uses collected data to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Enable merchants to operate their stores</li>
                <li>Process customer orders</li>
                <li>Provide app functionality</li>
                <li>Deliver notifications / updates</li>
                <li>Prevent fraud</li>
                <li>Improve platform performance</li>
                <li>Provide support to merchants and customers</li>
              </ul>
              <div className="bg-night-warm border border-night-line rounded-lg p-4 mt-4">
                <p className="font-semibold text-mist">Zuntly does not sell personal information to anyone.</p>
              </div>
            </div>
          </div>

          {/* Section 4: Sharing of Information */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">🤝</span>
              4. Sharing of Information
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>We share data only with:</p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✔</span>
                  <div>
                    <p className="font-semibold text-mist">Merchants</p>
                    <p className="text-sm">To fulfill customer orders.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✔</span>
                  <div>
                    <p className="font-semibold text-mist">Payment Gateways</p>
                    <p className="text-sm">For secure transaction processing.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✔</span>
                  <div>
                    <p className="font-semibold text-mist">Delivery Partners</p>
                    <p className="text-sm">To deliver orders.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✔</span>
                  <div>
                    <p className="font-semibold text-mist">Third-party service providers</p>
                    <p className="text-sm">(SMS/Email/WhatsApp, analytics)</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✔</span>
                  <div>
                    <p className="font-semibold text-mist">Legal authorities</p>
                    <p className="text-sm">(Only when legally required)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Data Security */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">🔒</span>
              5. Data Security
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>Zuntly implements:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>SSL encryption</li>
                <li>Secure cloud hosting</li>
                <li>Password encryption</li>
                <li>Limited access to sensitive data</li>
                <li>Regular audits</li>
              </ul>
            </div>
          </div>

          {/* Section 6: Data Retention */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">⏱️</span>
              6. Data Retention
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Customer data is stored as long as their account is active.</li>
                <li>Merchant store data may be retained for regulatory compliance.</li>
                <li>Users may request deletion of personal data anytime.</li>
              </ul>
            </div>
          </div>

          {/* Section 7: User Rights */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">👤</span>
              7. User Rights
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>Users have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Access personal data</li>
                <li>Request corrections</li>
                <li>Request deletion</li>
                <li>Withdraw consent</li>
                <li>Know how their data is used</li>
              </ul>
              <p className="mt-4 font-medium">Requests can be submitted via the merchant or Zuntly support.</p>
            </div>
          </div>

          {/* Section 8: Children's Privacy */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">👶</span>
              8. Children's Privacy
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>Zuntly does not knowingly collect data from children under 13.</p>
              <p>Any accidental data will be deleted upon request.</p>
            </div>
          </div>

          {/* Section 9: Policy Updates */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">🔄</span>
              9. Policy Updates
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>We may update this Privacy Policy.</p>
              <p>Revised versions will be posted with a new effective date.</p>
            </div>
          </div>

          {/* Section 10: Contact Information */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📧</span>
              10. Contact Information
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>For privacy-related concerns:</p>
              <div className="bg-night-warm rounded-lg p-6 border border-night-line space-y-2">
                <p className="font-bold text-mist text-lg">Zuntly Commerce System</p>
                <p className="font-semibold text-mist-soft">ZUNVENT SOLUTIONS PRIVATE LIMITED</p>
                <p className="text-mist-soft flex items-start">
                  <span className="mr-2">📍</span>
                  <span>9-418/A, Nuzividu Road, ST Colony, Vissannapet, Krishna, Vissannapet, Andhra Pradesh, India, 521215</span>
                </p>
                <p className="text-mist-soft flex items-center">
                  <span className="mr-2">📧</span>
                  <span>Email: ecom@zuntly.com</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
